/**
 * Agent-file card — the visual layer for the `attach_file` channel, shared by
 * every Agent SDK door (admin console, Quality Co-Pilot, Tech Support,
 * Analyst portal).
 *
 * Renders one agent-generated file as a card in the chat: images (charts)
 * preview inline; every file gets a Download button. Bytes come through the
 * door's authenticated download endpoint via the shared axios instance (the
 * blob becomes an object URL), so nothing here needs signed URLs or inline
 * dispositions. `base` is the door's API prefix (e.g. '/quality-copilot' on
 * the shared axios instance) — the download endpoint exists on every door via
 * the console-door factory.
 *
 * Telemetry (kanban 6da08127): taking delivery of an agent-built file is the
 * outcome the engagement audit wants to count per conversation, so the card
 * records a portal_events row for every Download click (with its outcome) and
 * every full-size preview open. Both go through trackEventNow because the card
 * also renders on admin pages that never start the portal flush timer.
 */

import { useEffect, useRef, useState } from 'react';
import { downloadConversationAttachment } from '../../services/agentConsole';
import { trackEventNow } from '../../services/portalTracking';
import { triggerBlobDownload } from '../../utils/triggerDownload';
import {
  fileKindIcon,
  fileKindLabel,
  formatFileSize,
  isPreviewableImage,
  type AgentFileData,
} from './agentFileCards';
import styles from './AgentFileCard.module.css';

export function AgentFileCard({
  base,
  file,
  conversationId,
}: {
  /** Door API prefix on the shared axios instance, e.g. '/quality-copilot'. */
  base: string;
  file: AgentFileData;
  /** Null before the conversation exists — cards only render inside one. */
  conversationId: string | null;
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The fetched blob is kept so Download reuses the preview's bytes.
  const blobRef = useRef<Blob | null>(null);

  const canPreview = isPreviewableImage(file.mimeType) && !previewFailed;

  useEffect(() => {
    if (!conversationId || !isPreviewableImage(file.mimeType)) return;
    let cancelled = false;
    let url: string | null = null;
    downloadConversationAttachment(base, conversationId, file.attachmentId)
      .then((blob) => {
        if (cancelled) return;
        blobRef.current = blob;
        url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      })
      .catch(() => {
        if (!cancelled) setPreviewFailed(true);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [base, conversationId, file.attachmentId, file.mimeType]);

  // Shared shape for both events so one query groups deliveries per
  // conversation, door and file type.
  const eventMeta = (conversationId: string) => ({
    conversation_id: conversationId,
    attachment_id: file.attachmentId,
    door: base,
    mime_type: file.mimeType,
    size_bytes: file.sizeBytes,
  });

  const onDownload = async () => {
    if (!conversationId || downloading) return;
    setDownloading(true);
    setError(null);
    let outcome: 'ok' | 'error' = 'ok';
    try {
      const blob =
        blobRef.current ??
        (await downloadConversationAttachment(base, conversationId, file.attachmentId));
      blobRef.current = blob;
      const url = URL.createObjectURL(blob);
      triggerBlobDownload(url, file.filename);
      URL.revokeObjectURL(url);
    } catch {
      outcome = 'error';
      setError('Download failed — try again.');
    } finally {
      setDownloading(false);
      trackEventNow('click', 'agent_file_download', { ...eventMeta(conversationId), outcome });
    }
  };

  const onPreviewOpen = () => {
    if (!conversationId) return;
    trackEventNow('click', 'agent_file_preview_open', eventMeta(conversationId));
  };

  return (
    <div className={styles.card} data-testid="agent-file-card">
      {canPreview && previewUrl && (
        <a
          className={styles.previewLink}
          href={previewUrl}
          target="_blank"
          rel="noopener noreferrer"
          title="Open full size in a new tab"
          onClick={onPreviewOpen}
          data-testid="agent-file-preview-link"
        >
          <img className={styles.preview} src={previewUrl} alt={file.title} />
        </a>
      )}
      <div className={styles.row}>
        <span className={styles.kindIcon} aria-hidden="true">
          {fileKindIcon(file.mimeType)}
        </span>
        <div className={styles.meta}>
          <span className={styles.title}>{file.title}</span>
          <span className={styles.sub} data-testid="agent-file-sub">
            <span className={styles.kind}>{fileKindLabel(file.mimeType)}</span> · {file.filename} ·{' '}
            {formatFileSize(file.sizeBytes)}
          </span>
        </div>
        <button
          type="button"
          className={styles.downloadBtn}
          onClick={onDownload}
          disabled={!conversationId || downloading}
          data-testid="agent-file-download"
        >
          {downloading ? 'Downloading…' : '⬇ Download'}
        </button>
      </div>
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
}
